<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Tag from 'primevue/tag'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { useAppToast } from '@/shared/lib/useAppToast'
import ExcelProductTypeDraftReviewPanel from './ExcelProductTypeDraftReviewPanel.vue'
import ArticleTemplateEditor from './ArticleTemplateEditor.vue'
import ProductDraftPreviewPanel from './ProductDraftPreviewPanel.vue'
import { getAllBrands } from '@/modules/admin/brands/api/brands.service'
import { getAllCategories } from '@/modules/admin/categories/api/categories.service'
import { getAllAttributes } from '@/modules/admin/products/attributes/api/attributes.service'
import {
  confirmColumnMapping, getColumnMapping, getConfirmedColumnMapping,
  getMappedImportRows, validateColumnMapping
} from '../api/imports.service'
import { getExcelProductTypeTransitionMetadata } from '../api/excelProductTypeDrafts.service'

const props = defineProps({
  jobId: { type: String, required: true },
  details: { type: Object, default: null }
})
const toast = useAppToast()
const sourceFileId = ref(null)
const sheetOrder = ref(0)
const mapping = ref(null)
const confirmed = ref(null)
const activeStep = ref('columns')
const productPreview = ref(null)
function confirmLeave() { return productPreview.value?.confirmLeave() ?? true }
defineExpose({ confirmLeave })
function switchStep(step) {
  if (step === activeStep.value) return
  if (activeStep.value === 'products' && !confirmLeave()) return
  activeStep.value = step
}
const mappedPreview = ref(null)
const state = ref('idle')
const error = ref(null)
const validation = ref(null)
const transformation = ref(null)
const modelCode = ref(null)
const articleTemplate = ref(null)
const selectedProductTypeDrafts = ref([])
const productTypeTransitionMetadata = ref(null)
const selectedProductTypeSelections = ref([])
const loadingSelectedProductTypeDrafts = ref(false)
let transitionSequence = 0
const productTypeFields = ref(null)
const brands = ref([])
const categories = ref([])
const articleAttributes = ref([])
const articleAttributesLoading = ref(false)
const articleAttributesLoaded = ref(false)
const articleAttributesError = ref(null)
let articleAttributesPromise = null
const catalogProductTypeSourceOptions = [
  { label: 'Игнорировать', value: 0 },
  { label: 'Колонка Excel', value: 1 },
  { label: 'Существующее значение', value: 3 }
]
let sequence = 0
const beginRequest = () => ({ sequence: ++sequence, jobId: props.jobId, sourceFileId: sourceFileId.value, sheetOrder: sheetOrder.value })
const isCurrent = request => request.sequence === sequence && request.jobId === props.jobId &&
  request.sourceFileId === sourceFileId.value && request.sheetOrder === sheetOrder.value
function fail(request, e) {
  if (!isCurrent(request)) return
  const message = apiErrorMessage(e)
  error.value = { ...e, message }; state.value = 'error'; toastError(message)
}
const targetOptions = [
  { key: 'Article', label: 'Article' }, { key: 'Name', label: 'Name' },
  { key: 'Description', label: 'Description' }, { key: 'ModelCode', label: 'Model code' },
  { key: 'CollectionName', label: 'Collection name' }, { key: 'Quantity', label: 'Quantity' },
  { key: 'BasePrice', label: 'Base price' }, { key: 'Brand', label: 'Brand' },
  { key: 'Category', label: 'Category' }, { key: 'PriceVariant', label: 'Price variant' },
  { key: 'Ignore', label: 'Ignore' }, { key: 'Unmapped', label: 'Unmapped' }
]
const simpleProductTypeSourceOptions = [
  { label: 'Игнорировать', value: 0 },
  { label: 'Колонка Excel', value: 1 }
]

const modelCodeSourceOptions = [
  { label: 'Игнорировать', value: 0 },
  { label: 'Колонка Excel', value: 1 },
  { label: 'Преобразование Article', value: 2 }
]

// Numeric values match ModelCodeSourceMode / ModelCodeRuleType in backend contracts.
const MODEL_CODE_SOURCE = Object.freeze({ ExcelColumn: 0, ArticleRule: 1, LegacyArticleTransformation: 2 })
const MODEL_CODE_RULE = Object.freeze({ FixedLength: 0, LeadingLettersAndDigits: 1 })
// Numeric values mirror ArticleTemplatePartType in backend contracts.
const ARTICLE_TEMPLATE_PART_TYPE = Object.freeze({ ModelCode: 0, AttributeValueCode: 1, Literal: 2 })
const modelCodeConfigurationOptions = [
  { label: 'Существующая настройка (legacy)', value: null },
  { label: 'Из Article по правилу', value: MODEL_CODE_SOURCE.ArticleRule },
  { label: 'Из колонки Excel', value: MODEL_CODE_SOURCE.ExcelColumn },
  { label: 'Legacy Article transformation', value: MODEL_CODE_SOURCE.LegacyArticleTransformation }
]
const modelCodeRuleOptions = [
  { label: 'Фиксированная длина', value: MODEL_CODE_RULE.FixedLength },
  { label: 'Буквы в начале + цифры', value: MODEL_CODE_RULE.LeadingLettersAndDigits }
]

const productTypeColumnOptions = computed(() =>
  columns.value.map(column => ({
    label:
      column.rawHeader ||
      column.normalizedHeader ||
      column.sourceColumnLetter ||
      `Колонка ${column.sourceColumnIndex}`,
    value: column.sourceColumnIndex
  }))
)
const toastError = value => toast.error(typeof value === 'string' ? value : value?.message || 'Не удалось выполнить запрос.')
const sourceOptions = computed(() => (props.details?.sources ?? []).map(x => ({ ...x, label: x.originalFileName })))
const columns = computed(() => mapping.value?.columns ?? [])
const articleColumn = computed(() => columns.value.find(x => targetFor(x) === 'Article'))
const explicitModelCode = computed(() => columns.value.some(x => targetFor(x) === 'ModelCode'))
const transformationEnabled = computed(() => {
  if (!transformation.value || !articleColumn.value) {
    return false
  }

  if (productTypeFields.value) {
    return productTypeFields.value.name?.mode === 2 || productTypeFields.value.modelCode?.mode === 2
  }

  return !explicitModelCode.value
})
const brandOptions = computed(() =>
  brands.value.map(brand => ({
    label: brand.name,
    value: brand.id
  }))
)

const categoryOptions = computed(() =>
  categories.value.map(category => ({
    label: category.name,
    value: category.id
  }))
)
const hasBlockingIssues = computed(() => (validation.value?.issues ?? []).some(x => x.severity === 2 || x.severity === 'Error'))
const modelCodeValidationMessage = computed(() => {
  if (!modelCode.value) return null
  if (modelCode.value.source === MODEL_CODE_SOURCE.ArticleRule) {
    if (modelCode.value.rule === MODEL_CODE_RULE.FixedLength && !(Number(modelCode.value.length) > 0))
      return 'Укажите количество символов больше нуля.'
    if (modelCode.value.rule === MODEL_CODE_RULE.LeadingLettersAndDigits &&
        !(Number.isInteger(modelCode.value.digitsCount) && modelCode.value.digitsCount >= 0))
      return 'Укажите неотрицательное количество цифр после букв.'
  }
  if (modelCode.value.source === MODEL_CODE_SOURCE.ExcelColumn && modelCode.value.excelColumnIndex == null)
    return 'Выберите колонку Excel с ModelCode.'
  return null
})
const articleTemplateValidationMessage = computed(() => {
  if (!articleTemplate.value) return null
  const parts = Array.isArray(articleTemplate.value.parts) ? articleTemplate.value.parts : []
  if (parts.length === 0) return 'Добавьте хотя бы одну часть шаблона.'
  const modelCodeParts = parts.filter(part => part.type === ARTICLE_TEMPLATE_PART_TYPE.ModelCode)
  if (modelCodeParts.length !== 1) return 'В шаблоне должна быть ровно одна часть ModelCode.'
  for (const part of parts) {
    if (part.type === ARTICLE_TEMPLATE_PART_TYPE.AttributeValueCode) {
      const id = Number(part.productAttributeId)
      if (!Number.isInteger(id) || id <= 0) return 'Выберите характеристику для каждой части типа «Характеристика».'
      if (!articleAttributesLoaded.value || !articleAttributes.value.some(attribute => Number(attribute.id) === id))
        return `Характеристика #${id} недоступна. Выберите существующую характеристику.`
    }
    if (part.type === ARTICLE_TEMPLATE_PART_TYPE.Literal &&
        (part.literal === null || part.literal === undefined || part.literal === ''))
      return 'Постоянный текст не может быть пустым.'
    if (![ARTICLE_TEMPLATE_PART_TYPE.ModelCode, ARTICLE_TEMPLATE_PART_TYPE.AttributeValueCode,
      ARTICLE_TEMPLATE_PART_TYPE.Literal].includes(part.type))
      return 'Шаблон содержит неизвестный тип части.'
  }
  return null
})
const canConfirm = computed(() => validation.value && !hasBlockingIssues.value &&
  !modelCodeValidationMessage.value && !articleTemplateValidationMessage.value && state.value !== 'confirming')

function issueFor(column) {
  return [...(mapping.value?.issues ?? []), ...(validation.value?.issues ?? [])]
    .filter(x => x.sourceColumnIndex === column.sourceColumnIndex)
}
function targetFor(column) {
  if (column.state === 'Ignored' || column.state === 2) return 'Ignore'
  if (column.state === 'Unmapped' || column.state === 0) return 'Unmapped'
  if (column.targetField == null) return 'Unmapped'
  if (typeof column.targetField === 'number') return targetOptions[column.targetField]?.key || 'Unmapped'
  return column.targetField
}
function markMappingDirty() {
  sequence++
  activeStep.value = 'columns'
  validation.value = null
  confirmed.value = null
  mappedPreview.value = null
  selectedProductTypeDrafts.value = []
  selectedProductTypeSelections.value = []
  if (mapping.value) state.value = 'loaded'
}
function markTransformationDirty() {
  sequence++
  validation.value = null
  mappedPreview.value = null
  confirmed.value = null
  selectedProductTypeDrafts.value = []
  selectedProductTypeSelections.value = []
  if (mapping.value) state.value = 'loaded'
}
function setModelCodeSource(source) {
  if (source == null) modelCode.value = null
  else if (source === MODEL_CODE_SOURCE.ArticleRule) modelCode.value = {
    source, excelColumnIndex: null, rule: MODEL_CODE_RULE.LeadingLettersAndDigits, length: null, digitsCount: null
  }
  else modelCode.value = { source, excelColumnIndex: null, rule: null, length: null, digitsCount: null }
  if (source === MODEL_CODE_SOURCE.LegacyArticleTransformation && !transformation.value) {
    transformation.value = { operation: 1, position: 1, from: '', to: '' }
  }
  markMappingDirty()
}
function setModelCodeRule(rule) {
  if (!modelCode.value) return
  modelCode.value = { source: MODEL_CODE_SOURCE.ArticleRule, excelColumnIndex: null,
    rule, length: null, digitsCount: null }
  markMappingDirty()
}
function setModelCodeParameter(field, value) {
  if (!modelCode.value) return
  modelCode.value = { ...modelCode.value, [field]: value }
  markMappingDirty()
}
function modelCodePayload() {
  if (!modelCode.value) return null
  const source = modelCode.value.source
  const isArticleRule = source === MODEL_CODE_SOURCE.ArticleRule
  return {
    source,
    excelColumnIndex: source === MODEL_CODE_SOURCE.ExcelColumn ? modelCode.value.excelColumnIndex : null,
    rule: isArticleRule ? modelCode.value.rule : null,
    length: isArticleRule && modelCode.value.rule === MODEL_CODE_RULE.FixedLength ? modelCode.value.length : null,
    digitsCount: isArticleRule && modelCode.value.rule === MODEL_CODE_RULE.LeadingLettersAndDigits
      ? modelCode.value.digitsCount
      : null
  }
}
function updateArticleTemplate(value) {
  articleTemplate.value = value
  markMappingDirty()
  if (value) void ensureArticleAttributes()
}
function ensureArticleAttributes() {
  if (articleAttributesLoaded.value) return Promise.resolve()
  if (articleAttributesPromise) return articleAttributesPromise
  articleAttributesLoading.value = true
  articleAttributesError.value = null
  articleAttributesPromise = (async () => {
    try {
      const response = await getAllAttributes()
      const rows = Array.isArray(response) ? response
        : Array.isArray(response?.items) ? response.items
          : Array.isArray(response?.data) ? response.data : []
      articleAttributes.value = rows.map(attribute => ({
        id: Number(attribute.id ?? attribute.Id ?? attribute.productAttributeId),
        name: attribute.name ?? attribute.Name ?? `Характеристика #${attribute.id ?? attribute.Id ?? attribute.productAttributeId}`
      }))
      articleAttributesLoaded.value = true
    } catch (e) {
      articleAttributesError.value = e?.message || 'Не удалось загрузить список характеристик.'
    } finally {
      articleAttributesLoading.value = false
      articleAttributesPromise = null
    }
  })()
  return articleAttributesPromise
}
function apiErrorMessage(e) {
  const data = e?.response?.data
  const nestedErrors = Array.isArray(data?.errors)
    ? data.errors.flatMap(item => Array.isArray(item.errors) ? item.errors : [item.message]).filter(Boolean)
    : []
  const message = typeof data === 'string' ? data : nestedErrors[0] || data?.detail || data?.message || data?.error || e?.message
  const rowError = message?.match(/row\s+(\d+):\s+[^()]+\((.*)\)/i)
  return rowError ? `Не удалось определить ModelCode в строке ${rowError[1]}: ${rowError[2]}` : message
}
function changeTransformation(field, value) {
  if (!transformation.value) return
  const next = { ...transformation.value, [field]: value }
  if (field === 'operation') {
    next.value = value === 1 ? null : ''
    next.from = value === 1 ? '' : null
    next.to = value === 1 ? '' : null
  }
  transformation.value = next
  markTransformationDirty()
}
function transformationPayload() {
  const current = transformation.value
  if (!current) return null
  const base = { operation: current.operation, position: current.position }
  return current.operation === 1
    ? { ...base, from: current.from || '', to: current.to || '' }
    : { ...base, value: current.value || '' }
}
function createDefaultProductTypeFields() {
  const fromTechnicalTarget = target => {
    const column = columns.value.find(x => targetFor(x) === target)

    return column
      ? {
          mode: 1,
          sourceColumnIndex: column.sourceColumnIndex,
          existingValueId: null
        }
      : {
          mode: 0,
          sourceColumnIndex: null,
          existingValueId: null
        }
  }

  return {
    name: fromTechnicalTarget('Name'),
    modelCode: fromTechnicalTarget('ModelCode'),
    description: fromTechnicalTarget('Description'),
    collection: fromTechnicalTarget('CollectionName'),
    brand: fromTechnicalTarget('Brand'),
    category: fromTechnicalTarget('Category')
  }
}
function setTarget(column, key) {
  const wasArticle = targetFor(column) === 'Article'
  if (key === 'Ignore') {
    column.state = 'Ignored'; column.targetField = null; column.variantName = null; column.isUserConfirmed = true
  } else if (key === 'Unmapped') {
    column.state = 'Unmapped'; column.targetField = null; column.variantName = null; column.isUserConfirmed = true
  } else {
    column.state = 'Mapped'; column.targetField = key; column.isUserConfirmed = true
    if (key === 'PriceVariant' && !column.variantName) column.variantName = column.rawHeader || column.normalizedHeader || ''
    if (key !== 'PriceVariant') column.variantName = null
  }
  if (wasArticle && key !== 'Article') transformation.value = null
  markMappingDirty()
}
function setProductTypeExistingValue(fieldName, existingValueId) {
  if (!productTypeFields.value) return

  productTypeFields.value[fieldName] = {
    ...productTypeFields.value[fieldName],
    sourceColumnIndex: null,
    existingValueId
  }

  markProductTypeFieldsDirty()
}
function setProductTypeFieldMode(fieldName, mode) {
  if (!productTypeFields.value) {
    productTypeFields.value = createDefaultProductTypeFields()
  }

  productTypeFields.value[fieldName] = {
    mode,
    sourceColumnIndex: null,
    existingValueId: null
  }

  if ((fieldName === 'name' || fieldName === 'modelCode') && mode === 2 && !transformation.value) {
    transformation.value = {
      operation: 1,
      position: 1,
      from: '',
      to: ''
    }
  }

  if ((fieldName === 'name' || fieldName === 'modelCode') &&
      productTypeFields.value.name?.mode !== 2 && productTypeFields.value.modelCode?.mode !== 2) {
    transformation.value = null
  }
    markProductTypeFieldsDirty()
}
async function loadProductTypeCatalogs() {
  const [brandsResult, categoriesResult] = await Promise.allSettled([
    getAllBrands(),
    getAllCategories()
  ])

  brands.value =
    brandsResult.status === 'fulfilled' && Array.isArray(brandsResult.value)
      ? brandsResult.value
      : []

  categories.value =
    categoriesResult.status === 'fulfilled' && Array.isArray(categoriesResult.value)
      ? categoriesResult.value
      : []
}
function setProductTypeFieldColumn(fieldName, sourceColumnIndex) {
  if (!productTypeFields.value) return

  productTypeFields.value[fieldName] = {
    ...productTypeFields.value[fieldName],
    sourceColumnIndex,
    existingValueId: null
  }

  markProductTypeFieldsDirty()
}
function markProductTypeFieldsDirty() {
  sequence++
  activeStep.value = 'columns'
  validation.value = null
  confirmed.value = null
  mappedPreview.value = null
  selectedProductTypeDrafts.value = []
  selectedProductTypeSelections.value = []

  if (mapping.value) state.value = 'loaded'
}
function setVariantName(column, value) {
  column.variantName = value
  markMappingDirty()
}
function confidenceSeverity(value) { return value === 'Exact' || value === 3 ? 'success' : value === 'High' || value === 2 ? 'info' : value === 'Low' || value === 1 ? 'warn' : 'secondary' }
function statusLabel(column) { return issueFor(column).length ? 'Needs review' : column.isUserConfirmed ? 'Confirmed' : 'Suggested' }
function statusSeverity(column) { return issueFor(column).length ? 'danger' : column.isUserConfirmed ? 'success' : 'info' }
function displayValue(value) { return value?.value?.normalizedValue ?? value?.value?.normalizedDecimal ?? '—' }
function payload() {
  return {
    columns: columns.value.map(column => ({
      sourceColumnIndex: column.sourceColumnIndex,
      state: typeof column.state === 'number'
        ? column.state
        : ({ Unmapped: 0, Mapped: 1, Ignored: 2 }[column.state] ?? 0),
      targetField: column.targetField == null
        ? null
        : (typeof column.targetField === 'number'
            ? column.targetField
            : targetOptions.findIndex(x => x.key === column.targetField)),
      isUserConfirmed: !!column.isUserConfirmed,
      variantName:
        column.targetField === 'PriceVariant' || column.targetField === 9
          ? column.variantName || null
          : null
    })),
    articleModelCodeTransformation:
      transformationEnabled.value ? transformationPayload() : null,
    productTypeFields: productTypeFields.value,
    modelCode: modelCodePayload(),
    articleTemplate: articleTemplate.value
  }
}
async function load() {
  const request = beginRequest()
  activeStep.value = 'columns'
  state.value = 'loading'; error.value = null; confirmed.value = null; mappedPreview.value = null; validation.value = null
  mapping.value = null
  transformation.value = null
  modelCode.value = null
  articleTemplate.value = null
  productTypeFields.value = null
  selectedProductTypeDrafts.value = []
  selectedProductTypeSelections.value = []
  if (!request.sourceFileId) { state.value = 'empty'; return }
  try {
    const result = await getColumnMapping(request.jobId, request.sourceFileId, request.sheetOrder)
    if (!isCurrent(request)) return
    mapping.value = result
    await loadProductTypeCatalogs()
    if (!isCurrent(request)) return
    try {
      const revision = await getConfirmedColumnMapping(request.jobId, request.sourceFileId, request.sheetOrder)
      if (!isCurrent(request)) return
      confirmed.value = revision
    } catch (e) { if (!isCurrent(request)) return; if (e.status !== 404) throw e }
    if (!isCurrent(request)) return
    if (confirmed.value?.columns?.length) mapping.value.columns = confirmed.value.columns
    transformation.value = confirmed.value?.articleModelCodeTransformation ?? null
    modelCode.value = confirmed.value?.modelCode ?? null
    articleTemplate.value = confirmed.value?.articleTemplate ?? null
    if (articleTemplate.value) await ensureArticleAttributes()
    if (!isCurrent(request)) return
    productTypeFields.value = confirmed.value
      ? (confirmed.value.productTypeFields ?? null)
      : createDefaultProductTypeFields()
    state.value = confirmed.value ? 'confirmed' : 'loaded'
    if (confirmed.value) await loadMappedPreview(request)
  } catch (e) { fail(request, e) }
}
async function validate() {
  const request = beginRequest()
  state.value = 'validating'
  try {
    const result = await validateColumnMapping(request.jobId, request.sourceFileId, request.sheetOrder, payload())
    if (!isCurrent(request)) return
    validation.value = result; state.value = 'validated'
  } catch (e) { fail(request, e) }
}
async function confirm() {
  if (!canConfirm.value) return
  const request = beginRequest()
  state.value = 'confirming'
  try {
    const result = await confirmColumnMapping(request.jobId, request.sourceFileId, request.sheetOrder, payload())
    if (!isCurrent(request)) return
    confirmed.value = result
    modelCode.value = confirmed.value.modelCode ?? modelCode.value
    articleTemplate.value = confirmed.value.articleTemplate ?? articleTemplate.value
    mapping.value.columns = confirmed.value.columns
    productTypeFields.value = confirmed.value.productTypeFields ?? productTypeFields.value
    state.value = 'confirmed'
    toast.success(`Сопоставление подтверждено — revision ${confirmed.value.revisionNumber}`)
    await loadMappedPreview(request)
  } catch (e) { fail(request, e) }
}
async function loadMappedPreview(request) {
  if (!isCurrent(request)) return
  state.value = 'previewLoading'
  try {
    const result = await getMappedImportRows(request.jobId, request.sourceFileId, request.sheetOrder)
    if (!isCurrent(request)) return
    mappedPreview.value = result; state.value = 'confirmed'
  } catch (e) { fail(request, e) }
}
function updateProductTypeSelection(selection) {
  const next = (selection || []).map(x => ({ ...x }))
  const previous = new Map(selectedProductTypeDrafts.value.map(x => [x.excelProductTypeDraftId, x]))
  if (next.length !== previous.size || next.some(x => {
    const saved = previous.get(x.excelProductTypeDraftId)
    return !saved || saved.expectedVersion !== x.concurrencyVersion || saved.existingProductTypeId !== (x.existingProductTypeId ?? null)
  })) {
    selectedProductTypeDrafts.value = []
    productTypeTransitionMetadata.value = null
  }
  selectedProductTypeSelections.value = next
}
async function openProductPreview(selection) {
  const currentRevisionId = confirmed.value?.revisionId
  if (!currentRevisionId || !selection?.length) return
  const currentJobId = props.jobId
  const request = ++transitionSequence
  loadingSelectedProductTypeDrafts.value = true
  try {
    const ids = selection.map(x => x.excelProductTypeDraftId)
    const metadata = await getExcelProductTypeTransitionMetadata(currentJobId, currentRevisionId, ids)
    if (request !== transitionSequence || props.jobId !== currentJobId || confirmed.value?.revisionId !== currentRevisionId) return
    selectedProductTypeSelections.value = selection.map(x => ({ ...x }))
    selectedProductTypeDrafts.value = selection.map(x => ({
      excelProductTypeDraftId: x.excelProductTypeDraftId,
      expectedVersion: x.concurrencyVersion,
      existingProductTypeId: x.existingProductTypeId ?? null
    }))
    productTypeTransitionMetadata.value = metadata
    activeStep.value = 'products'
  } catch (e) {
    if (request !== transitionSequence || props.jobId !== currentJobId || confirmed.value?.revisionId !== currentRevisionId) return
    error.value = e
    toastError(e)
  } finally { if (request === transitionSequence) loadingSelectedProductTypeDrafts.value = false }
}
watch(() => props.details?.sources, value => {
  if (!value?.some(x => x.id === sourceFileId.value)) sourceFileId.value = value?.[0]?.id ?? null
}, { immediate: true })
watch([() => props.jobId, sourceFileId, sheetOrder], load, { immediate: true, flush: 'sync' })
onBeforeUnmount(() => { sequence++ })
</script>

<template>
  <div class="mapping-step">
    <div class="stepper" aria-label="Этапы импорта">
      <span class="active">
        <b>1</b> Файл
      </span>
      <i class="pi pi-angle-right"></i>

      <button
        type="button"
        :class="{ active: activeStep === 'columns' }"
        @click="switchStep('columns')"
      >
        <b>2</b> Колонки
      </button>
      <i class="pi pi-angle-right"></i>

      <button
        type="button"
        :disabled="!confirmed || state !== 'confirmed'"
        :class="{ active: activeStep === 'types' }"
        @click="switchStep('types')"
      >
        <b>3</b> Типы товаров
      </button>
      <i class="pi pi-angle-right"></i>

      <button
        type="button"
        :disabled="!confirmed || !selectedProductTypeDrafts.length"
        :class="{ active: activeStep === 'products' }"
        @click="switchStep('products')"
      >
        <b>4</b> Товары
      </button>
    </div>

    <ExcelProductTypeDraftReviewPanel
      v-if="activeStep === 'types' && confirmed"
      :key="confirmed.revisionId"
      :job-id="jobId"
      :revision-id="confirmed.revisionId"
      :selection="selectedProductTypeSelections"
      @selection-change="updateProductTypeSelection"
      @preview="openProductPreview"
    />

    <ProductDraftPreviewPanel
      v-else-if="activeStep === 'products' && confirmed"
      ref="productPreview"
      :key="`${jobId}:${confirmed.revisionId}`"
      :job-id="jobId"
      :revision-id="confirmed.revisionId"
      :drafts="selectedProductTypeDrafts"
      :transition-metadata="productTypeTransitionMetadata"
    />

    <Card v-else>
      <template #title>
        Сопоставление колонок Excel
      </template>

      <template #subtitle>
        Проверьте предложения и назначьте смысл каждой физической колонке.
      </template>

      <template #content>
        <div class="toolbar">
          <label>
            Файл
            <Dropdown
              v-model="sourceFileId"
              :options="sourceOptions"
              option-label="label"
              option-value="id"
              placeholder="Выберите файл"
            />
          </label>

          <label>
            Лист
            <InputNumber
              v-model="sheetOrder"
              :min="0"
              :use-grouping="false"
            />
          </label>

          <div class="actions">
            <Button
              label="Проверить сопоставление"
              icon="pi pi-check"
              outlined
              :loading="state === 'validating'"
              :disabled="!mapping || state === 'loading' || !!articleTemplateValidationMessage"
              @click="validate"
            />

            <Button
              label="Подтвердить сопоставление"
              icon="pi pi-save"
              :loading="state === 'confirming'"
              :disabled="!canConfirm"
              @click="confirm"
            />
          </div>
        </div>

        <div
          v-if="state === 'loading' || state === 'idle'"
          class="loading"
        >
          <i class="pi pi-spin pi-spinner"></i>
          Загружаем предложения…
        </div>

        <div
          v-else-if="state === 'empty'"
          class="empty"
        >
          Сначала загрузите и разберите Excel-файл.
        </div>

        <div
          v-else-if="state === 'error'"
          class="empty error"
        >
          {{ error?.message || 'Не удалось загрузить mapping.' }}
          <Button
            label="Повторить"
            text
            @click="load"
          />
        </div>

        <template v-else>
          <div
            v-if="confirmed"
            class="confirmed-banner"
          >
            <i class="pi pi-check-circle"></i>
            Сопоставление подтверждено — revision {{ confirmed.revisionNumber }}

            <Button
              label="К типам товаров"
              text
              :disabled="state !== 'confirmed'"
              @click="activeStep = 'types'"
            />
          </div>

          <section class="model-code-card">
            <h3>Формирование ModelCode</h3>
            <p class="hint">Выберите правило для Article или колонку Excel с готовым ModelCode. Legacy mapping сохраняет существующую настройку.</p>
            <div class="model-code-fields">
              <label>
                Источник ModelCode
                <Dropdown
                  :model-value="modelCode?.source ?? null"
                  :options="modelCodeConfigurationOptions"
                  option-label="label"
                  option-value="value"
                  @update:model-value="setModelCodeSource"
                />
              </label>

              <label v-if="modelCode?.source === MODEL_CODE_SOURCE.ArticleRule">
                Правило
                <Dropdown
                  :model-value="modelCode.rule"
                  :options="modelCodeRuleOptions"
                  option-label="label"
                  option-value="value"
                  @update:model-value="setModelCodeRule"
                />
              </label>

              <label v-if="modelCode?.source === MODEL_CODE_SOURCE.ArticleRule && modelCode.rule === MODEL_CODE_RULE.FixedLength">
                Количество символов
                <InputNumber
                  :model-value="modelCode.length"
                  :min="1"
                  :use-grouping="false"
                  @update:model-value="setModelCodeParameter('length', $event)"
                />
              </label>

              <label v-else-if="modelCode?.source === MODEL_CODE_SOURCE.ArticleRule && modelCode.rule === MODEL_CODE_RULE.LeadingLettersAndDigits">
                Количество цифр после букв
                <InputNumber
                  :model-value="modelCode.digitsCount"
                  :min="0"
                  :use-grouping="false"
                  @update:model-value="setModelCodeParameter('digitsCount', $event)"
                />
              </label>

              <label v-else-if="modelCode?.source === MODEL_CODE_SOURCE.ExcelColumn">
                Колонка Excel
                <Dropdown
                  :model-value="modelCode.excelColumnIndex"
                  :options="productTypeColumnOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Выберите колонку"
                  @update:model-value="setModelCodeParameter('excelColumnIndex', $event)"
                />
              </label>

              <span v-else-if="modelCode?.source === MODEL_CODE_SOURCE.LegacyArticleTransformation" class="hint">
                Используется существующая настройка преобразования Article.
              </span>
            </div>
            <small v-if="modelCodeValidationMessage" class="issue">{{ modelCodeValidationMessage }}</small>
          </section>

          <section v-if="validation?.issues?.some(issue => issue.sourceColumnIndex == null)" class="mapping-validation-errors">
            <strong>Ошибки проверки mapping</strong>
            <span v-for="(issue, index) in validation.issues.filter(x => x.sourceColumnIndex == null)" :key="`${issue.code}-${index}`" class="issue">
              {{ issue.message }}
            </span>
          </section>

          <section v-if="validation?.modelCodePreview?.length" class="model-code-preview">
            <h3>Предварительный просмотр ModelCode</h3>
            <DataTable :value="validation.modelCodePreview" responsive-layout="scroll" striped-rows>
              <Column field="sourceRowNumber" header="Строка" />
              <Column field="article" header="Article" />
              <Column header="ModelCode">
                <template #body="{ data }">{{ data.modelCode || '—' }}</template>
              </Column>
              <Column header="Статус">
                <template #body="{ data }">
                  <Tag :value="data.isValid ? 'OK' : 'Ошибка'" :severity="data.isValid ? 'success' : 'danger'" />
                  <small v-if="!data.isValid" class="issue">{{ data.errorMessage || data.errorCode }}</small>
                </template>
              </Column>
            </DataTable>
          </section>

          <ArticleTemplateEditor
            :model-value="articleTemplate"
            :attributes="articleAttributes"
            :loading="articleAttributesLoading"
            :load-error="articleAttributesError"
            :validation-message="articleTemplateValidationMessage"
            :disabled="state === 'validating' || state === 'confirming'"
            @update:model-value="updateArticleTemplate"
            @retry-attributes="ensureArticleAttributes"
          />
          <p v-if="articleTemplate && !modelCode" class="hint article-template-model-code-hint">
            Шаблон содержит ModelCode, но способ его формирования не настроен. При необходимости настройте его отдельно выше.
          </p>

          <!-- ProductType field -> source mapping -->
          <section
            v-if="productTypeFields"
            class="product-type-fields-card"
          >
            <h3>Настройка полей типа товара</h3>

            <p class="hint">
              Выберите источник для каждого поля типа товара.
              Одна колонка Excel может использоваться для нескольких полей.
            </p>

            <!-- Name / Description / Collection -->
            <div
              v-for="field in [
                { key: 'name', label: 'Name' },
                { key: 'description', label: 'Description' },
                { key: 'collection', label: 'Collection' }
              ]"
              :key="field.key"
              class="product-type-field-row"
            >
              <strong>{{ field.label }}</strong>

              <Dropdown
                :model-value="productTypeFields[field.key].mode"
                :options="field.key === 'name' ? modelCodeSourceOptions : simpleProductTypeSourceOptions"
                option-label="label"
                option-value="value"
                @update:model-value="setProductTypeFieldMode(field.key, $event)"
              />

              <Dropdown
                v-if="productTypeFields[field.key].mode === 1"
                :model-value="productTypeFields[field.key].sourceColumnIndex"
                :options="productTypeColumnOptions"
                option-label="label"
                option-value="value"
                placeholder="Выберите колонку"
                @update:model-value="setProductTypeFieldColumn(field.key, $event)"
              />

              <span v-else-if="field.key === 'name' && productTypeFields.name.mode === 2" class="hint">
                Используется исходный Article и настроенное преобразование
              </span>

              <span
                v-else
                class="hint"
              >
                Значение не импортируется
              </span>
            </div>

            <!-- ModelCode -->
            <div v-if="!modelCode" class="product-type-field-row">
              <strong>ModelCode</strong>

              <Dropdown
                :model-value="productTypeFields.modelCode.mode"
                :options="modelCodeSourceOptions"
                option-label="label"
                option-value="value"
                @update:model-value="setProductTypeFieldMode('modelCode', $event)"
              />

              <Dropdown
                v-if="productTypeFields.modelCode.mode === 1"
                :model-value="productTypeFields.modelCode.sourceColumnIndex"
                :options="productTypeColumnOptions"
                option-label="label"
                option-value="value"
                placeholder="Выберите колонку"
                @update:model-value="setProductTypeFieldColumn('modelCode', $event)"
              />

              <span
                v-else-if="productTypeFields.modelCode.mode === 2"
                class="hint"
              >
                Используется исходный Article и настроенное преобразование
              </span>

              <span
                v-else
                class="hint"
              >
                ModelCode не импортируется
              </span>
            </div>

            <!-- Brand -->
            <div class="product-type-field-row">
              <strong>Brand</strong>

              <Dropdown
                :model-value="productTypeFields.brand.mode"
                :options="catalogProductTypeSourceOptions"
                option-label="label"
                option-value="value"
                @update:model-value="setProductTypeFieldMode('brand', $event)"
              />

              <Dropdown
                v-if="productTypeFields.brand.mode === 1"
                :model-value="productTypeFields.brand.sourceColumnIndex"
                :options="productTypeColumnOptions"
                option-label="label"
                option-value="value"
                placeholder="Выберите колонку"
                @update:model-value="setProductTypeFieldColumn('brand', $event)"
              />

              <Dropdown
                v-else-if="productTypeFields.brand.mode === 3"
                :model-value="productTypeFields.brand.existingValueId"
                :options="brandOptions"
                option-label="label"
                option-value="value"
                placeholder="Выберите существующий бренд"
                @update:model-value="setProductTypeExistingValue('brand', $event)"
              />

              <span
                v-else
                class="hint"
              >
                Brand не импортируется
              </span>
            </div>

            <!-- Category -->
            <div class="product-type-field-row">
              <strong>Category</strong>

              <Dropdown
                :model-value="productTypeFields.category.mode"
                :options="catalogProductTypeSourceOptions"
                option-label="label"
                option-value="value"
                @update:model-value="setProductTypeFieldMode('category', $event)"
              />

              <Dropdown
                v-if="productTypeFields.category.mode === 1"
                :model-value="productTypeFields.category.sourceColumnIndex"
                :options="productTypeColumnOptions"
                option-label="label"
                option-value="value"
                placeholder="Выберите колонку"
                @update:model-value="setProductTypeFieldColumn('category', $event)"
              />

              <Dropdown
                v-else-if="productTypeFields.category.mode === 3"
                :model-value="productTypeFields.category.existingValueId"
                :options="categoryOptions"
                option-label="label"
                option-value="value"
                placeholder="Выберите существующую категорию"
                @update:model-value="setProductTypeExistingValue('category', $event)"
              />

              <span
                v-else
                class="hint"
              >
                Category не импортируется
              </span>
            </div>
          </section>

          <!-- Article -> ModelCode transformation -->
          <section
            v-if="articleColumn && transformationEnabled"
            class="transformation-card"
          >
            <h3>Преобразование Article для Name / ModelCode</h3>

            <p>
              Для выбранных полей ProductType используется столбец
              «{{ articleColumn.rawHeader || articleColumn.normalizedHeader || articleColumn.sourceColumnLetter }}».
            </p>

            <div class="transformation-fields">
              <label>
                Операция

                <Dropdown
                  :model-value="transformation.operation"
                  :options="[
                    { label: 'Добавить', value: 0 },
                    { label: 'Заменить', value: 1 },
                    { label: 'Удалить', value: 2 }
                  ]"
                  option-label="label"
                  option-value="value"
                  @update:model-value="changeTransformation('operation', $event)"
                />
              </label>

              <label>
                Позиция

                <Dropdown
                  :model-value="transformation.position"
                  :options="[
                    {
                      label: transformation.operation === 0
                        ? 'В начало'
                        : transformation.operation === 1
                          ? 'В начале'
                          : 'Из начала',
                      value: 0
                    },
                    {
                      label: transformation.operation === 0
                        ? 'В конец'
                        : transformation.operation === 1
                          ? 'В конце'
                          : 'Из конца',
                      value: 1
                    }
                  ]"
                  option-label="label"
                  option-value="value"
                  @update:model-value="changeTransformation('position', $event)"
                />
              </label>

              <label
                v-if="transformation.operation === 0 || transformation.operation === 2"
              >
                {{ transformation.operation === 0 ? 'Строка для добавления' : 'Что удалить' }}

                <InputText
                  :model-value="transformation.value"
                  @update:model-value="changeTransformation('value', $event)"
                />
              </label>

              <template v-else>
                <label>
                  Что заменить

                  <InputText
                    :model-value="transformation.from"
                    @update:model-value="changeTransformation('from', $event)"
                  />
                </label>

                <label>
                  На что заменить

                  <InputText
                    :model-value="transformation.to"
                    @update:model-value="changeTransformation('to', $event)"
                  />
                </label>
              </template>
            </div>
          </section>

          <!-- Existing technical mapping -->
          <DataTable
            :value="columns"
            data-key="sourceColumnIndex"
            responsive-layout="scroll"
            striped-rows
          >
            <Column
              field="sourceColumnLetter"
              header="Колонка"
            />

            <Column
              field="rawHeader"
              header="Заголовок"
            />

            <Column header="Примеры">
              <template #body="{ data }">
                <span class="examples">
                  {{ (data.exampleValues || []).join(', ') || '—' }}
                </span>
              </template>
            </Column>

            <Column header="Назначение">
              <template #body="{ data }">
                <Dropdown
                  :model-value="targetFor(data)"
                  :options="targetOptions"
                  option-label="label"
                  option-value="key"
                  @update:model-value="setTarget(data, $event)"
                />
              </template>
            </Column>

            <Column header="Вариант">
              <template #body="{ data }">
                <InputText
                  v-if="targetFor(data) === 'PriceVariant'"
                  :model-value="data.variantName"
                  placeholder="Variant name"
                  :class="{ invalid: !data.variantName }"
                  @update:model-value="setVariantName(data, $event)"
                />

                <span v-else>—</span>
              </template>
            </Column>

            <Column header="Уверенность">
              <template #body="{ data }">
                <Tag
                  :value="data.confidence || 'Unknown'"
                  :severity="confidenceSeverity(data.confidence)"
                />
              </template>
            </Column>

            <Column header="Статус">
              <template #body="{ data }">
                <Tag
                  :value="statusLabel(data)"
                  :severity="statusSeverity(data)"
                />

                <small
                  v-for="issue in issueFor(data)"
                  :key="issue.code + issue.message"
                  class="issue"
                >
                  {{ issue.message }}
                </small>
              </template>
            </Column>
          </DataTable>

          <!-- Mapped preview -->
          <section
            v-if="mappedPreview"
            class="mapped-preview"
          >
            <h3>Результат сопоставления</h3>

            <DataTable
              :value="mappedPreview.rows"
              responsive-layout="scroll"
              striped-rows
            >
              <Column
                field="sourceRowNumber"
                header="#"
              />

              <Column header="Исходный Article">
                <template #body="{ data }">
                  {{ displayValue(data.article) }}
                </template>
              </Column>

              <Column header="ModelCode">
                <template #body="{ data }">
                  {{ displayValue(data.modelCode) }}
                </template>
              </Column>

              <Column header="Name">
                <template #body="{ data }">
                  {{ displayValue(data.name) }}
                </template>
              </Column>

              <Column header="Base price">
                <template #body="{ data }">
                  {{ displayValue(data.basePrice) }}
                </template>
              </Column>

              <Column header="Price variants">
                <template #body="{ data }">
                  {{
                    (data.priceVariants || [])
                      .map(x => `${x.variantName}: ${displayValue(x.value)}`)
                      .join(', ') || '—'
                  }}
                </template>
              </Column>

              <Column header="Проблемы">
                <template #body="{ data }">
                  <span
                    v-for="issue in data.issues || []"
                    :key="issue.code + issue.message"
                    class="issue"
                  >
                    {{ issue.message }}
                  </span>
                </template>
              </Column>
            </DataTable>
          </section>
        </template>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.stepper button { font: inherit; color: inherit; background: none; border: 0; padding: 0; cursor: pointer; white-space: nowrap; }.stepper button:disabled { color: #aaa; cursor: default; }
.mapping-step { display: grid; gap: 16px; }.stepper { display: flex; align-items: center; gap: 9px; color: #666; font-size: 13px; overflow-x: auto; }.stepper span { white-space: nowrap; }.stepper b { display: inline-grid; place-items: center; width: 24px; height: 24px; border-radius: 50%; background: #e8e8e5; margin-right: 5px; }.stepper .active { color: #171717; font-weight: 600; }.stepper .active b { background: #171717; color: white; }.stepper .disabled { color: #aaa; }.toolbar { display: flex; align-items: end; flex-wrap: wrap; gap: 12px; margin-bottom: 18px; }.toolbar label { display: grid; gap: 6px; font-size: 12px; font-weight: 600; }.toolbar :deep(.p-dropdown) { min-width: 220px; }.actions { margin-left: auto; display: flex; gap: 8px; flex-wrap: wrap; }.loading, .empty { padding: 56px; text-align: center; color: #777; }.error { color: #b42318; }.confirmed-banner { display: flex; gap: 8px; align-items: center; color: #18794e; background: #eaf8f0; padding: 11px 14px; border-radius: 10px; margin-bottom: 14px; }.examples { display: block; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.issue { display: block; color: #b42318; font-size: 11px; margin-top: 4px; }.invalid { border-color: #b42318 !important; }.mapped-preview { margin-top: 24px; }.mapped-preview h3 { margin: 0 0 12px; font-size: 18px; }
.product-type-fields-card {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
}

.model-code-card, .model-code-preview, .mapping-validation-errors {
  margin: 0 0 16px;
  padding: 16px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
}
.model-code-card h3, .model-code-preview h3 { margin: 0 0 8px; }
.model-code-card p { margin: 0 0 12px; }
.model-code-fields { display: flex; align-items: end; flex-wrap: wrap; gap: 12px; }
.model-code-fields label { display: grid; gap: 6px; min-width: 220px; font-size: 12px; font-weight: 600; }
.model-code-fields :deep(.p-dropdown) { min-width: 220px; }
.mapping-validation-errors { border-color: #f1b7b3; background: #fff8f7; }
.mapping-validation-errors .issue { font-size: 13px; }

.product-type-fields-card h3 {
  margin: 0 0 6px;
}

.product-type-fields-card > .hint {
  margin: 0 0 16px;
}

.product-type-field-row {
  display: grid;
  grid-template-columns: 140px minmax(180px, 220px) minmax(220px, 1fr);
  gap: 12px;
  align-items: center;
  margin-top: 10px;
}

.hint {
  color: #777;
  font-size: 12px;
}
@media (max-width: 800px) { .product-type-field-row {
  grid-template-columns: 1fr;
} .actions { margin-left: 0; width: 100%; }.toolbar :deep(.p-dropdown) { min-width: 180px; } }
</style>
